import React, { useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function ConnectionTab() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState('');
  const [error, setError] = useState('');
  const [popupRef, setPopupRef] = useState(null);
  const [mcpUrlInput, setMcpUrlInput] = useState('');

  const fetchStatus = useCallback(async () => {
    const res = await fetch(`${API_URL}/api/campfront/oauth/status`);
    const data = await res.json();
    setStatus(data);
    if (data.mcp_server_url && !mcpUrlInput) setMcpUrlInput(data.mcp_server_url);
  }, [mcpUrlInput]);

  useEffect(() => { fetchStatus(); const i = setInterval(fetchStatus, 5000); return () => clearInterval(i); }, [fetchStatus]);

  // Listen for postMessage from the OAuth popup callback page.
  useEffect(() => {
    const handler = (ev) => {
      if (!ev.data || typeof ev.data !== 'object') return;
      if (ev.data.type === 'campfront-oauth-success') {
        setError('');
        setLoading('');
        fetchStatus();
        if (popupRef && !popupRef.closed) { try { popupRef.close(); } catch(e) {} }
      } else if (ev.data.type === 'campfront-oauth-error') {
        setError('Campfront says you are not signed in. The popup is showing the sign-in page — sign in there, then click Connect here again.');
        setLoading('');
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [fetchStatus, popupRef]);

  const saveMcpUrl = async () => {
    setLoading('mcpurl'); setError('');
    try {
      const res = await fetch(`${API_URL}/api/campfront/mcp-url`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({mcp_server_url: mcpUrlInput.trim()})
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed');
      await fetchStatus();
    } catch (e) { setError(e.message); }
    setLoading('');
  };

  const resetClient = async () => {
    setLoading('reset'); setError('');
    try {
      const res = await fetch(`${API_URL}/api/campfront/oauth/register?force=true`, { method: 'POST' });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed');
      await fetchStatus();
    } catch (e) { setError(e.message); }
    setLoading('');
  };

  const connect = async () => {
    setLoading('connect'); setError('');
    // Open a blank popup SYNCHRONOUSLY on the user click so popup blockers allow it.
    const popup = window.open('about:blank', 'campfront-oauth', 'width=600,height=720,menubar=no,toolbar=no,location=yes');
    if (!popup) {
      setError('Popup blocked. Please allow popups for this site and try again.');
      setLoading('');
      return;
    }
    setPopupRef(popup);
    try {
      // Force-refresh client_id every time so we never use a stale/revoked DCR client.
      const regRes = await fetch(`${API_URL}/api/campfront/oauth/register?force=true`, { method: 'POST' });
      if (!regRes.ok) throw new Error((await regRes.json()).detail || 'Register failed');
      const res = await fetch(`${API_URL}/api/campfront/oauth/start`);
      const data = await res.json();
      if (!data.authorize_url) throw new Error(data.detail || 'No authorize URL');
      popup.location.href = data.authorize_url;
    } catch (e) {
      try { popup.close(); } catch(_) {}
      setError(e.message);
      setLoading('');
    }
  };

  const refresh = async () => {
    setLoading('refresh'); setError('');
    try {
      await fetch(`${API_URL}/api/campfront/oauth/manual-refresh`, { method: 'POST' });
      await fetchStatus();
    } catch (e) { setError(e.message); }
    setLoading('');
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect from Campfront?')) return;
    await fetch(`${API_URL}/api/campfront/oauth/disconnect`, { method: 'POST' });
    await fetchStatus();
  };

  if (!status) return <div style={styles.loading}>Loading...</div>;

  return (
    <div>
      <div style={{...styles.statusBanner, background: status.connected ? '#052e16' : '#450a0a', borderColor: status.connected ? '#22c55e' : '#dc2626'}}>
        <div style={{fontSize:20,fontWeight:700,color: status.connected ? '#4ade80' : '#f87171'}}>{status.connected ? 'Connected' : 'Not Connected'}</div>
        {status.connected && <div style={{color:'#9ca3af',fontSize:12,marginTop:4}}>Authorized: {new Date(status.authorized_at).toLocaleString()}</div>}
      </div>

      {error && <div style={styles.error}>{error}</div>}

      {!status.connected && (
        <div style={styles.steps}>
          <div style={{background:'#1e3a8a',borderRadius:10,padding:12,marginBottom:8,fontSize:12,color:'#bfdbfe',lineHeight:1.6}}>
            <b style={{color:'white'}}>Where do I get my MCP URL?</b><br/>
            In Campfront, go to <b>Camp Settings → Integrations → MCP</b>. Copy the <b>MCP server URL</b> shown there (looks like <code style={{background:'rgba(0,0,0,0.3)',padding:'1px 4px',borderRadius:3}}>https://mcp.campfront.com/&lt;your-id&gt;</code>) and paste it below.
          </div>

          <div style={styles.step}>
            <div style={styles.stepNum}>1</div>
            <div style={{flex:1}}>
              <div style={styles.stepTitle}>MCP Server URL</div>
              <div style={styles.stepDesc}>From Camp Settings → Integrations → MCP</div>
              <input
                data-testid="cf-mcp-url-input"
                value={mcpUrlInput}
                onChange={(e) => setMcpUrlInput(e.target.value)}
                placeholder="https://mcp.campfront.com/your-tenant-id"
                style={{width:'100%',marginTop:8,padding:'8px 10px',borderRadius:6,border:'1px solid #374151',background:'#0f172a',color:'white',fontSize:12,fontFamily:'monospace'}}
              />
            </div>
            <button data-testid="cf-save-mcp-url-btn" onClick={saveMcpUrl} disabled={!!loading || !mcpUrlInput.trim()} style={styles.btn}>
              {loading === 'mcpurl' ? 'Saving...' : (status.mcp_server_url === mcpUrlInput.trim() ? 'Saved' : 'Save URL')}
            </button>
          </div>

          <div style={{...styles.step, opacity: status.mcp_server_url ? 1 : 0.5}}>
            <div style={styles.stepNum}>2</div>
            <div style={{flex:1}}>
              <div style={styles.stepTitle}>Connect to Campfront</div>
              <div style={styles.stepDesc}>Opens a popup that auto-completes the OAuth handshake. You will be asked to log in / authorize.</div>
            </div>
            <button data-testid="cf-connect-btn" onClick={connect} disabled={!!loading || !status.mcp_server_url} style={styles.btn}>
              {loading === 'connect' ? 'Connecting...' : 'Connect'}
            </button>
          </div>

          {status.client_registered && (
            <div style={{display:'flex',justifyContent:'flex-end',marginTop:4}}>
              <button data-testid="cf-reset-client-btn" onClick={resetClient} disabled={!!loading} style={{...styles.smallBtn, background:'#7f1d1d'}}>
                {loading === 'reset' ? 'Resetting...' : 'Reset OAuth Client'}
              </button>
            </div>
          )}
        </div>
      )}

      {status.connected && (
        <div style={styles.detailGrid}>
          <Detail label="MCP Server URL" value={status.mcp_server_url} />
          <Detail label="Scope" value={status.scope} />
          <Detail label="Last Refreshed" value={status.last_refreshed_at ? new Date(status.last_refreshed_at).toLocaleString() : 'Never'} />
          <Detail label="Token Expires" value={status.access_token_expires_at ? new Date(status.access_token_expires_at).toLocaleString() : '?'} />
          <Detail label="Refresh Count" value={status.refresh_count} />
          <Detail label="Refresh Failures" value={status.refresh_failure_count} warn={status.refresh_failure_count > 0} />
          {status.last_refresh_error && <Detail label="Last Error" value={status.last_refresh_error} warn />}
          <div style={{display:'flex',gap:8,marginTop:16}}>
            <button onClick={refresh} disabled={!!loading} style={styles.btn}>{loading === 'refresh' ? 'Refreshing...' : 'Refresh Token Now'}</button>
            <button onClick={disconnect} style={{...styles.btn, background:'#dc2626'}}>Disconnect</button>
          </div>
        </div>
      )}
    </div>
  );
}

function FieldMappingTab() {
  const [mapping, setMapping] = useState(null);
  const [loading, setLoading] = useState(false);
  const [values, setValues] = useState({});
  const [savedMsg, setSavedMsg] = useState('');

  const fetchMapping = async () => {
    const res = await fetch(`${API_URL}/api/campfront/field-mapping`);
    setMapping(await res.json());
  };
  useEffect(() => { fetchMapping(); }, []);

  const discover = async () => {
    setLoading('discover');
    await fetch(`${API_URL}/api/campfront/discover-fields`, { method: 'POST' });
    await fetchMapping();
    setValues({}); // reset overrides so DB values show
    setLoading('');
  };

  const fieldGroups = [
    {
      title: 'Identifiers',
      items: [
        { key: 'person_id_field_id',             label: 'Camper ID',              source: 'PersonID' },
        { key: 'last_name_field_id',             label: 'Last Name',              source: 'Last Name' },
        { key: 'first_name_field_id',            label: 'First Name',             source: 'First Name' },
        { key: 'enrolled_sessions_field_id',     label: 'Enrolled Child Sessions',source: 'Enrolled Child Sessions' },
      ],
    },
    {
      title: 'AM Transport',
      items: [
        { key: 'am_dropoff_method_field_id',     label: 'AM Drop-off Method',     source: 'Trans-AMDropOffMethod' },
        { key: 'am_bus_field_id',                label: 'AM Bus Number',          source: '2026Transportation M AM Bus' },
        { key: 'pickup_address_field_id',        label: 'Pickup Address',         source: 'Trans-PickUpAddress' },
        { key: 'pickup_town_field_id',           label: 'Pickup Town',            source: 'Trans-PickUpTown' },
        { key: 'pickup_zip_field_id',            label: 'Pickup Zip',             source: 'Trans-PickUpZip' },
      ],
    },
    {
      title: 'PM Transport',
      items: [
        { key: 'pm_dismissal_method_field_id',   label: 'PM Dismissal Method',    source: 'Trans-PMDismissalMethod' },
        { key: 'diff_dropoff_address_field_id',  label: 'Different Drop-off Address', source: 'Trans-Diff.DropOffAddress' },
        { key: 'pm_bus_field_id',                label: 'PM Bus Number',          source: '2026Transportation M PM Bus' },
        { key: 'dropoff_address_field_id',       label: 'Drop-off Address',       source: 'Trans-DropOffAddress' },
        { key: 'dropoff_town_field_id',          label: 'Drop-off Town',          source: 'Trans-DropOffTown' },
        { key: 'dropoff_zip_field_id',           label: 'Drop-off Zip',           source: 'Trans-DropOffZip' },
      ],
    },
  ];

  const allItems = fieldGroups.flatMap(g => g.items);

  // Effective value for an input: explicit user override > DB value > source default
  const valueOf = (item) => {
    if (item.key in values) return values[item.key];
    if (mapping && mapping[item.key]) return mapping[item.key];
    return item.source;
  };

  const onChange = (key, v) => setValues(prev => ({...prev, [key]: v}));

  const mapAll = async () => {
    setLoading('map'); setSavedMsg('');
    const payload = {};
    for (const item of allItems) {
      const v = valueOf(item);
      if (v && v.trim()) payload[item.key] = v.trim();
    }
    const res = await fetch(`${API_URL}/api/campfront/field-mapping/bulk`, {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({mappings: payload}),
    });
    const data = await res.json();
    if (res.ok) {
      setSavedMsg(`Saved ${data.count} field mappings.`);
      setValues({});
      await fetchMapping();
    } else {
      setSavedMsg(`Error: ${data.detail || 'failed'}`);
    }
    setLoading('');
  };

  const resetAll = () => { setValues({}); setSavedMsg(''); };

  return (
    <div>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14,gap:8,flexWrap:'wrap'}}>
        <div style={{color:'#9ca3af',fontSize:12,flex:1,minWidth:200}}>
          Each input is pre-filled with the source column name. After you upload to Campfront, click <b style={{color:'white'}}>Map All Fields</b> to save them. Edit any input first if a Campfront ID differs.
        </div>
        <div style={{display:'flex',gap:6}}>
          <button data-testid="cf-reset-overrides-btn" onClick={resetAll} disabled={!!loading} style={{...styles.smallBtn, background:'#374151'}}>Reset</button>
          <button data-testid="cf-rediscover-btn" onClick={discover} disabled={!!loading} style={{...styles.smallBtn, background:'#475569'}}>
            {loading === 'discover' ? 'Discovering...' : 'Auto-Discover'}
          </button>
          <button data-testid="cf-map-all-btn" onClick={mapAll} disabled={!!loading} style={{...styles.btn, background:'#16a34a'}}>
            {loading === 'map' ? 'Saving...' : 'Map All Fields'}
          </button>
        </div>
      </div>

      {savedMsg && <div style={{background:'#052e16',border:'1px solid #16a34a',borderRadius:8,padding:10,color:'#4ade80',fontSize:13,marginBottom:12}}>{savedMsg}</div>}

      {mapping?.warnings?.length > 0 && mapping.warnings.map((w, i) => (
        <div key={i} style={{...styles.error, marginBottom:8}}>{w}</div>
      ))}

      {fieldGroups.map(group => (
        <div key={group.title} style={{marginBottom:18}}>
          <div style={{color:'#a78bfa',fontSize:13,fontWeight:700,marginBottom:6,textTransform:'uppercase',letterSpacing:0.5}}>{group.title}</div>
          <div style={{display:'flex',flexDirection:'column',gap:4}}>
            {group.items.map(f => {
              const v = valueOf(f);
              const saved = mapping?.[f.key];
              const dirty = (f.key in values) && values[f.key] !== saved;
              const borderColor = saved && saved === v ? '#16a34a' : dirty ? '#f59e0b' : '#374151';
              const textColor = saved && saved === v ? '#4ade80' : dirty ? '#fbbf24' : 'white';
              return (
                <div key={f.key} style={{background:'#1f2937',borderRadius:8,padding:10,display:'flex',alignItems:'center',gap:10}}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{color:'white',fontSize:13,fontWeight:500}}>{f.label}</div>
                    <div style={{color:'#6b7280',fontSize:10,fontFamily:'monospace',marginTop:2}}>{f.source}</div>
                  </div>
                  <input
                    data-testid={`cf-mapping-input-${f.key}`}
                    value={v}
                    onChange={(e) => onChange(f.key, e.target.value)}
                    style={{flex:'0 0 260px',padding:'6px 8px',borderRadius:5,border:`1px solid ${borderColor}`,background:'#0f172a',color:textColor,fontSize:11,fontFamily:'monospace'}}
                  />
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {mapping?.mapped_at && <div style={{color:'#6b7280',fontSize:11,marginTop:12}}>Last mapped: {new Date(mapping.mapped_at).toLocaleString()}</div>}
      {mapping?.discovered_at && <div style={{color:'#6b7280',fontSize:11,marginTop:4}}>Last auto-discovered: {new Date(mapping.discovered_at).toLocaleString()}</div>}
    </div>
  );
}

function TestPushPicker() {
  const [query, setQuery] = useState('');
  const [campers, setCampers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const t = setTimeout(async () => {
      const res = await fetch(`${API_URL}/api/campfront/campers-for-test?q=${encodeURIComponent(query)}&limit=20`);
      const data = await res.json();
      setCampers(data.campers || []);
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  const push = async () => {
    if (!selected) return;
    setLoading(true); setResult(null);
    const res = await fetch(`${API_URL}/api/campfront/test-push/${selected._id}`, { method: 'POST' });
    setResult(await res.json());
    setLoading(false);
  };

  return (
    <div>
      <input
        data-testid="cf-test-push-search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search by first or last name..."
        style={{width:'100%',padding:'8px 10px',borderRadius:6,border:'1px solid #78350f',background:'#0f172a',color:'white',fontSize:12,marginBottom:8}}
      />
      <div style={{maxHeight:160,overflowY:'auto',background:'#0f172a',borderRadius:6,marginBottom:8}}>
        {campers.length === 0 ? (
          <div style={{color:'#6b7280',padding:10,fontSize:12,fontStyle:'italic'}}>No campers</div>
        ) : campers.map(c => (
          <div
            key={c._id}
            onClick={() => setSelected(c)}
            style={{padding:'6px 10px',cursor:'pointer',fontSize:12,display:'flex',justifyContent:'space-between',
              background: selected?._id === c._id ? '#7c2d12' : 'transparent',
              borderBottom:'1px solid #1f2937', color: selected?._id === c._id ? 'white' : '#d1d5db'}}
          >
            <span>{c.first_name} {c.last_name} {c.campfront_enrollment_id ? <span style={{color:'#6b7280',fontSize:10}}>· cf={String(c.campfront_enrollment_id).substring(0,8)}…</span> : null}</span>
            <span style={{color:'#9ca3af',fontSize:11}}>AM:{c.am_bus_number || '—'} PM:{c.pm_bus_number || '—'} {c.campfront_enrollment_id ? '🔗' : ''}</span>
          </div>
        ))}
      </div>
      {selected && (
        <div style={{background:'#1c1917',borderRadius:6,padding:8,marginBottom:8,fontSize:11,color:'#fbbf24'}}>
          Selected: <b style={{color:'white'}}>{selected.first_name} {selected.last_name}</b> (AM={selected.am_bus_number || '—'}, PM={selected.pm_bus_number || '—'}, campfront_id={selected.campfront_enrollment_id || 'NOT LINKED — run Step 1 first'})
        </div>
      )}
      <button
        data-testid="cf-test-push-go-btn"
        onClick={push}
        disabled={!selected || loading}
        style={{...styles.btn, background:'#d97706', width:'100%'}}
      >
        {loading ? 'Pushing...' : 'Push This Camper to Campfront'}
      </button>
      {result && (
        <pre style={{marginTop:8,background:'#020617',color:'#e2e8f0',padding:10,borderRadius:6,fontSize:10,maxHeight:300,overflow:'auto'}}>
          {JSON.stringify(result, null, 2)}
        </pre>
      )}
    </div>
  );
}

function SyncTab() {
  const [pullResult, setPullResult] = useState(null);
  const [pushResult, setPushResult] = useState(null);
  const [initialResult, setInitialResult] = useState(null);
  const [initialProgress, setInitialProgress] = useState(null);
  const [pullAllResult, setPullAllResult] = useState(null);
  const [auditData, setAuditData] = useState(null);
  const [showAudit, setShowAudit] = useState(false);
  const [autoSync, setAutoSync] = useState(true);
  const [queueCount, setQueueCount] = useState(0);
  const [loading, setLoading] = useState('');

  useEffect(() => {
    fetch(`${API_URL}/api/campfront/auto-sync-status`).then(r => r.json()).then(d => setAutoSync(d.enabled));
    fetch(`${API_URL}/api/campfront/push-queue`).then(r => r.json()).then(d => setQueueCount(d.count || 0));
    // Resume progress polling if a bulk push is already running
    fetch(`${API_URL}/api/campfront/initial-push-progress`).then(r => r.json()).then(d => {
      if (d && (d.running || d.result)) {
        setInitialProgress(d);
        if (d.result) setInitialResult(d.result);
      }
    });
  }, []);

  const pullAllCampers = async () => {
    setLoading('pull-all');
    const res = await fetch(`${API_URL}/api/campfront/pull-all-campers`, { method: 'POST' });
    setPullAllResult(await res.json());
    setShowAudit(false);
    setLoading('');
  };

  const loadAudit = async () => {
    setLoading('audit');
    const res = await fetch(`${API_URL}/api/campfront/match-audit`);
    setAuditData(await res.json());
    setShowAudit(true);
    setLoading('');
  };

  const pull = async () => {
    setLoading('pull');
    const res = await fetch(`${API_URL}/api/campfront/pull-now`, { method: 'POST' });
    setPullResult(await res.json());
    setLoading('');
  };

  const pushAll = async () => {
    setLoading('push');
    const res = await fetch(`${API_URL}/api/campfront/push-all-pending`, { method: 'POST' });
    setPushResult(await res.json());
    setLoading('');
  };

  const initialPush = async () => {
    setInitialResult(null);
    await fetch(`${API_URL}/api/campfront/initial-push`, { method: 'POST' });
    setInitialProgress({running: true, pushed: 0, failed: 0, total: 0});
  };

  // Poll initial-push progress while running
  useEffect(() => {
    if (!initialProgress?.running) return;
    const i = setInterval(async () => {
      const res = await fetch(`${API_URL}/api/campfront/initial-push-progress`);
      const data = await res.json();
      setInitialProgress(data);
      if (!data.running) {
        if (data.result) setInitialResult(data.result);
        clearInterval(i);
      }
    }, 1500);
    return () => clearInterval(i);
  }, [initialProgress?.running]);

  const toggleSync = async () => {
    const newVal = !autoSync;
    await fetch(`${API_URL}/api/campfront/auto-sync?enabled=${newVal}`, { method: 'POST' });
    setAutoSync(newVal);
  };

  return (
    <div>
      {/* Step 1: Pull All Camper IDs first */}
      <div style={{background:'#0c4a6e',border:'2px solid #0284c7',borderRadius:10,padding:14,marginBottom:16}}>
        <div style={{color:'#7dd3fc',fontSize:14,fontWeight:700,marginBottom:4}}>Step 1 — Pull All Camper IDs (Link Records)</div>
        <div style={{color:'#9ca3af',fontSize:12,marginBottom:10}}>
          Fetches every Campfront enrollment, matches each one to a local camper by name, and stores the Campfront camper ID locally. No bus data is sent or received. Run this first so future pushes know where to write.
        </div>
        <button data-testid="cf-pull-all-btn" onClick={pullAllCampers} disabled={!!loading} style={{...styles.btn, background:'#0284c7', width:'100%'}}>
          {loading === 'pull-all' ? 'Pulling & Matching...' : 'Pull All Camper IDs from Campfront'}
        </button>
        {pullAllResult && (
          <div style={{marginTop:10,background:'#0f172a',borderRadius:8,padding:10,fontSize:12}}>
            <div style={{color:'#7dd3fc'}}>Campfront enrollments fetched: {pullAllResult.total_campfront_enrollments}</div>
            <div style={{color:'#4ade80'}}>Matched: {pullAllResult.matched} / {pullAllResult.total_local_campers}</div>
            <div style={{color:'#a78bfa'}}>Newly linked: {pullAllResult.newly_linked} | Already linked: {pullAllResult.already_linked}</div>
            <div style={{color:'#f59e0b'}}>Unmatched: {pullAllResult.unmatched_count}</div>
            <button data-testid="cf-view-audit-btn" onClick={loadAudit} disabled={!!loading} style={{...styles.smallBtn, background:'#475569', marginTop:8}}>
              {loading === 'audit' ? 'Loading...' : (showAudit ? 'Refresh Audit' : 'View Audit Record')}
            </button>
            {pullAllResult.unmatched_names?.length > 0 && (
              <details style={{marginTop:6}}>
                <summary style={{color:'#9ca3af',cursor:'pointer'}}>Show unmatched names</summary>
                <div style={{color:'#6b7280',marginTop:4,fontSize:11,lineHeight:1.6}}>{pullAllResult.unmatched_names.join(', ')}</div>
              </details>
            )}
          </div>
        )}
        {showAudit && auditData && !auditData.empty && (
          <div style={{marginTop:10,background:'#020617',borderRadius:8,padding:12,fontSize:11,border:'1px solid #1e293b'}}>
            <div style={{color:'#a78bfa',fontWeight:700,marginBottom:6}}>AUDIT RECORD · Run {auditData.run_id?.substring(0,8)} · {new Date(auditData.synced_at).toLocaleString()}</div>

            <div style={{color:'#7dd3fc',fontWeight:700,marginTop:8,marginBottom:4}}>Raw Campfront API response shape:</div>
            <pre style={{background:'#0f172a',padding:8,borderRadius:6,color:'#e2e8f0',fontSize:10,overflowX:'auto',whiteSpace:'pre-wrap'}}>{JSON.stringify(auditData.raw_response_samples, null, 2)}</pre>

            <div style={{color:'#7dd3fc',fontWeight:700,marginTop:8,marginBottom:4}}>First 10 Campfront names (what we received):</div>
            <pre style={{background:'#0f172a',padding:8,borderRadius:6,color:'#e2e8f0',fontSize:10,overflowX:'auto',whiteSpace:'pre-wrap'}}>{JSON.stringify(auditData.campfront_name_samples, null, 2)}</pre>

            <div style={{color:'#4ade80',fontWeight:700,marginTop:8,marginBottom:4}}>First 10 local camper names (what we have):</div>
            <pre style={{background:'#0f172a',padding:8,borderRadius:6,color:'#e2e8f0',fontSize:10,overflowX:'auto',whiteSpace:'pre-wrap'}}>{JSON.stringify(auditData.local_name_samples, null, 2)}</pre>

            <div style={{color:'#f59e0b',fontWeight:700,marginTop:8,marginBottom:4}}>Summary:</div>
            <pre style={{background:'#0f172a',padding:8,borderRadius:6,color:'#e2e8f0',fontSize:10,overflowX:'auto',whiteSpace:'pre-wrap'}}>{JSON.stringify(auditData.summary, null, 2)}</pre>
          </div>
        )}
        {showAudit && auditData?.empty && (
          <div style={{marginTop:10,color:'#9ca3af',fontSize:12,fontStyle:'italic'}}>{auditData.message}</div>
        )}
      </div>

      {/* Test push single camper */}
      <div style={{background:'#422006',border:'2px solid #d97706',borderRadius:10,padding:14,marginBottom:16}}>
        <div style={{color:'#fbbf24',fontSize:14,fontWeight:700,marginBottom:4}}>Test Push (Single Camper)</div>
        <div style={{color:'#9ca3af',fontSize:12,marginBottom:10}}>
          Pushes ONE camper's PersonID + AM/PM bus to the <b style={{color:'white'}}>imported data</b> form on Campfront. Returns the full Campfront response so you can verify the write worked before pushing everyone.
        </div>
        <TestPushPicker />
      </div>

      {/* Step 2: Initial Push */}
      <div style={{background:'#1e1b4b',border:'2px solid #7c3aed',borderRadius:10,padding:14,marginBottom:16}}>
        <div style={{color:'#a78bfa',fontSize:14,fontWeight:700,marginBottom:4}}>Step 2 — Initial Push (Send Bus Assignments)</div>
        <div style={{color:'#9ca3af',fontSize:12,marginBottom:10}}>Push every local camper's AM/PM bus assignment up to Campfront. Run Step 1 first.</div>
        <button data-testid="cf-initial-push-btn" onClick={initialPush} disabled={initialProgress?.running} style={{...styles.btn, background:'#7c3aed', width:'100%'}}>
          {initialProgress?.running ? `Pushing... ${initialProgress.pushed + initialProgress.failed}/${initialProgress.total || '?'}` : 'Push All Bus Assignments to Campfront'}
        </button>
        {initialProgress?.running && initialProgress.total > 0 && (
          <div style={{marginTop:10,background:'#0f172a',borderRadius:8,padding:10,fontSize:12}}>
            <div style={{color:'#a78bfa'}}>Pushed: {initialProgress.pushed} | Failed: {initialProgress.failed} / Total: {initialProgress.total}</div>
            <div style={{marginTop:6,background:'#1f2937',height:6,borderRadius:3,overflow:'hidden'}}>
              <div style={{width: `${Math.min(100, ((initialProgress.pushed + initialProgress.failed) / initialProgress.total) * 100)}%`, background:'#7c3aed', height:'100%', transition:'width 0.4s'}} />
            </div>
          </div>
        )}
        {initialResult && !initialProgress?.running && (
          <div style={{marginTop:10,background:'#0f172a',borderRadius:8,padding:10,fontSize:12}}>
            <div style={{color:'#4ade80'}}>Matched: {initialResult.matched} / {initialResult.total_local_campers} local campers</div>
            <div style={{color:'#a78bfa'}}>Pushed: {initialResult.pushed} | Failed: {initialResult.failed}</div>
            <div style={{color:'#f59e0b'}}>Unmatched: {initialResult.unmatched_count}</div>
            {initialResult.unmatched_names?.length > 0 && (
              <details style={{marginTop:6}}>
                <summary style={{color:'#9ca3af',cursor:'pointer'}}>Show unmatched names</summary>
                <div style={{color:'#6b7280',marginTop:4,fontSize:11,lineHeight:1.6}}>{initialResult.unmatched_names.join(', ')}</div>
              </details>
            )}
          </div>
        )}
      </div>

      <div style={{display:'flex',gap:8,marginBottom:16}}>
        <button data-testid="cf-pull-now-btn" onClick={pull} disabled={!!loading} style={{...styles.btn, flex:1}}>{loading === 'pull' ? 'Pulling...' : 'Pull Bus Campers Now'}</button>
        <button data-testid="cf-push-all-pending-btn" onClick={pushAll} disabled={!!loading} style={{...styles.btn, flex:1, background:'#7c3aed'}}>{loading === 'push' ? 'Pushing...' : 'Push All Pending'}</button>
      </div>

      {pullResult && (
        <div style={{background:'#052e16',borderRadius:8,padding:12,marginBottom:12,color:'#4ade80',fontSize:13}}>
          Pulled {pullResult.total} campers ({pullResult.inserted} new, {pullResult.updated} updated)
        </div>
      )}
      {pushResult && (
        <div style={{background:'#1e1b4b',borderRadius:8,padding:12,marginBottom:12,color:'#a78bfa',fontSize:13}}>
          Pushed: {pushResult.pushed} | Skipped: {pushResult.skipped} | Failed: {pushResult.failed}
        </div>
      )}

      <div style={{background:'#1f2937',borderRadius:8,padding:14,display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
        <div>
          <div style={{color:'white',fontSize:14,fontWeight:600}}>Auto-Sync</div>
          <div style={{color:'#9ca3af',fontSize:11}}>Push bus assignments to Campfront instantly</div>
        </div>
        <button onClick={toggleSync} style={{padding:'6px 16px',borderRadius:6,border:'none',fontWeight:700,fontSize:13,cursor:'pointer',background:autoSync?'#22c55e':'#374151',color:'white'}}>
          {autoSync ? 'ON' : 'OFF'}
        </button>
      </div>

      <div style={{background:'#1f2937',borderRadius:8,padding:14}}>
        <div style={{color:'white',fontSize:14,fontWeight:600}}>Push Queue</div>
        <div style={{color: queueCount > 0 ? '#f59e0b' : '#4ade80', fontSize:13, marginTop:4}}>{queueCount} item(s) pending</div>
      </div>
    </div>
  );
}

function CampersTab() {
  const [campers, setCampers] = useState([]);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetch(`${API_URL}/api/campfront/campers`).then(r => r.json()).then(d => setCampers(d.campers || []));
  }, []);

  const filtered = filter === 'unassigned'
    ? campers.filter(c => !c.campfront_arrival_bus && !c.campfront_departure_bus && !c.am_bus_number && !c.pm_bus_number)
    : campers;

  return (
    <div>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
        <div style={{color:'#9ca3af',fontSize:12}}>{filtered.length} camper(s)</div>
        <div style={{display:'flex',gap:4}}>
          <button onClick={() => setFilter('all')} style={{...styles.smallBtn, background: filter==='all'?'#2563eb':'#374151'}}>All</button>
          <button onClick={() => setFilter('unassigned')} style={{...styles.smallBtn, background: filter==='unassigned'?'#f59e0b':'#374151'}}>Needs Bus</button>
        </div>
      </div>
      <div style={{maxHeight:500,overflowY:'auto'}}>
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
          <thead>
            <tr style={{borderBottom:'1px solid #374151'}}>
              {['Name','Parent','Age','Arrival Addr','Departure Addr','AM Bus','PM Bus','Synced'].map(h => (
                <th key={h} style={{padding:'6px 4px',color:'#9ca3af',fontWeight:600,textAlign:'left'}}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((c, i) => (
              <tr key={i} style={{borderBottom:'1px solid #1f2937'}}>
                <td style={styles.td}>{c.campfront_name || `${c.first_name || ''} ${c.last_name || ''}`}</td>
                <td style={styles.td}>{c.campfront_parent_name || ''}</td>
                <td style={styles.td}>{c.campfront_age || ''}</td>
                <td style={{...styles.td,maxWidth:120,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{c.campfront_arrival_address || ''}</td>
                <td style={{...styles.td,maxWidth:120,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{c.campfront_departure_address || ''}</td>
                <td style={styles.td}>{c.campfront_arrival_bus || c.am_bus_number || <span style={{color:'#f87171'}}>—</span>}</td>
                <td style={styles.td}>{c.campfront_departure_bus || c.pm_bus_number || <span style={{color:'#f87171'}}>—</span>}</td>
                <td style={{...styles.td,fontSize:10,color:'#6b7280'}}>{c.campfront_last_pulled_at ? new Date(c.campfront_last_pulled_at).toLocaleDateString() : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CallLogTab() {
  const [calls, setCalls] = useState([]);
  const [stats, setStats] = useState([]);
  const [filterType, setFilterType] = useState('all');

  useEffect(() => {
    fetch(`${API_URL}/api/campfront/call-log?limit=100`).then(r => r.json()).then(d => setCalls(d.calls || []));
    fetch(`${API_URL}/api/campfront/call-stats`).then(r => r.json()).then(d => setStats(d.stats || []));
  }, []);

  const filtered = filterType === 'all' ? calls : filterType === 'fail' ? calls.filter(c => !c.success) : calls.filter(c => c.triggered_by === filterType);

  return (
    <div>
      {stats.length > 0 && (
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(140px,1fr))',gap:8,marginBottom:16}}>
          {stats.map(s => (
            <div key={s._id} style={{background:'#1f2937',borderRadius:8,padding:10}}>
              <div style={{color:'white',fontSize:12,fontWeight:600}}>{s._id}</div>
              <div style={{color:'#9ca3af',fontSize:11}}>{s.count} calls | {s.successes} ok | avg {Math.round(s.avg_duration_ms)}ms</div>
            </div>
          ))}
        </div>
      )}
      <div style={{display:'flex',gap:4,marginBottom:12}}>
        {['all','fail','auto_pull','auto_push','manual','health_check'].map(t => (
          <button key={t} onClick={() => setFilterType(t)} style={{...styles.smallBtn, background: filterType===t?'#2563eb':'#374151'}}>{t}</button>
        ))}
      </div>
      <div style={{maxHeight:400,overflowY:'auto'}}>
        {filtered.map((c, i) => (
          <div key={i} style={{background:'#1f2937',borderRadius:6,padding:8,marginBottom:4,fontSize:11}}>
            <div style={{display:'flex',justifyContent:'space-between'}}>
              <span style={{color:'white',fontWeight:600}}>{c.tool_name}</span>
              <span style={{color: c.success ? '#4ade80' : '#f87171'}}>{c.success ? 'OK' : 'FAIL'} · {c.duration_ms}ms</span>
            </div>
            <div style={{color:'#6b7280',fontSize:10}}>{new Date(c.called_at).toLocaleString()} · {c.triggered_by}</div>
            {c.error && <div style={{color:'#f87171',fontSize:10,marginTop:2}}>{c.error.substring(0, 200)}</div>}
          </div>
        ))}
        {filtered.length === 0 && <div style={{color:'#6b7280',textAlign:'center',padding:20}}>No calls logged yet</div>}
      </div>
    </div>
  );
}

function HealthTab() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(false);

  const check = async () => {
    setLoading(true);
    const res = await fetch(`${API_URL}/api/campfront/health`);
    setHealth(await res.json());
    setLoading(false);
  };

  useEffect(() => { check(); }, []);

  const statusColor = { healthy: '#4ade80', degraded: '#f59e0b', down: '#f87171' };

  return (
    <div>
      {health && (
        <>
          <div style={{textAlign:'center',padding:24,background:'#1f2937',borderRadius:12,marginBottom:16}}>
            <div style={{fontSize:36,fontWeight:700,color: statusColor[health.status] || '#6b7280'}}>{(health.status || 'unknown').toUpperCase()}</div>
            {health.duration_ms != null && <div style={{color:'#9ca3af',fontSize:12,marginTop:4}}>Latency: {health.duration_ms}ms</div>}
          </div>
          <div style={styles.detailGrid}>
            {health.tool_count != null && <Detail label="Tools Available" value={health.tool_count} />}
            {health.push_queue_size != null && <Detail label="Push Queue" value={health.push_queue_size} warn={health.push_queue_size > 0} />}
            {health.requires_review != null && <Detail label="Needs Review" value={health.requires_review} warn={health.requires_review > 0} />}
            {health.refresh_failures != null && <Detail label="Refresh Failures" value={health.refresh_failures} warn={health.refresh_failures > 0} />}
            {health.error && <Detail label="Error" value={health.error} warn />}
          </div>
        </>
      )}
      <button onClick={check} disabled={loading} style={{...styles.btn, width:'100%', marginTop:16}}>{loading ? 'Checking...' : 'Run Health Check Now'}</button>
    </div>
  );
}

function Detail({ label, value, warn }) {
  return (
    <div style={{padding:'8px 0',borderBottom:'1px solid #1f2937'}}>
      <div style={{color:'#9ca3af',fontSize:11}}>{label}</div>
      <div style={{color: warn ? '#f87171' : 'white', fontSize:14, fontWeight:500}}>{String(value ?? '—')}</div>
    </div>
  );
}

const TABS = [
  { id: 'connection', label: 'Connection' },
  { id: 'fields', label: 'Field Mapping' },
  { id: 'sync', label: 'Sync' },
  { id: 'campers', label: 'Campers' },
  { id: 'log', label: 'Call Log' },
  { id: 'health', label: 'Health' },
];

export default function CampfrontAdmin() {
  const [activeTab, setActiveTab] = useState('connection');

  const renderTab = () => {
    switch (activeTab) {
      case 'connection': return <ConnectionTab />;
      case 'fields': return <FieldMappingTab />;
      case 'sync': return <SyncTab />;
      case 'campers': return <CampersTab />;
      case 'log': return <CallLogTab />;
      case 'health': return <HealthTab />;
      default: return null;
    }
  };

  return (
    <div style={{minHeight:'100vh',background:'#111827',color:'white',fontFamily:'system-ui'}}>
      <div style={{background:'#1f2937',borderBottom:'1px solid #374151',padding:'16px 20px',display:'flex',alignItems:'center',gap:12}}>
        <button onClick={() => window.location.href = '/'} style={{background:'#374151',border:'none',color:'white',padding:'6px 12px',borderRadius:6,cursor:'pointer',fontSize:13}}>Back</button>
        <div>
          <h1 style={{margin:0,fontSize:20,fontWeight:700}}>Campfront Integration</h1>
          <p style={{margin:'4px 0 0',color:'#9ca3af',fontSize:12}}>Bus-Router &#8596; Campfront MCP Sync</p>
        </div>
      </div>

      <div style={{display:'flex',borderBottom:'1px solid #374151',overflowX:'auto'}}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            style={{padding:'12px 16px',border:'none',background:'transparent',color: activeTab===t.id?'#3b82f6':'#9ca3af',
              fontWeight: activeTab===t.id?700:400,fontSize:13,cursor:'pointer',borderBottom: activeTab===t.id?'2px solid #3b82f6':'2px solid transparent',whiteSpace:'nowrap'}}>
            {t.label}
          </button>
        ))}
      </div>

      <div style={{padding:20,maxWidth:900,margin:'0 auto'}}>
        {renderTab()}
      </div>
    </div>
  );
}

const styles = {
  loading: { color: '#9ca3af', textAlign: 'center', padding: 40 },
  statusBanner: { borderRadius: 10, padding: 16, marginBottom: 20, border: '2px solid', textAlign: 'center' },
  error: { background: '#450a0a', border: '1px solid #dc2626', borderRadius: 8, padding: 10, color: '#f87171', fontSize: 13 },
  steps: { display: 'flex', flexDirection: 'column', gap: 12 },
  step: { background: '#1f2937', borderRadius: 10, padding: 14, display: 'flex', alignItems: 'center', gap: 12 },
  stepNum: { width: 28, height: 28, borderRadius: '50%', background: '#2563eb', color: 'white', fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  stepTitle: { color: 'white', fontSize: 14, fontWeight: 600 },
  stepDesc: { color: '#9ca3af', fontSize: 12, marginTop: 2 },
  btn: { padding: '8px 16px', borderRadius: 8, border: 'none', background: '#2563eb', color: 'white', fontWeight: 600, fontSize: 13, cursor: 'pointer' },
  smallBtn: { padding: '4px 10px', borderRadius: 5, border: 'none', color: 'white', fontSize: 11, fontWeight: 600, cursor: 'pointer' },
  detailGrid: { background: '#1f2937', borderRadius: 10, padding: 16 },
  td: { padding: '6px 4px', color: '#d1d5db' },
};
